import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Box, Fab, InternalStandardProps, Theme } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { Layout } from "../Layout";
import {
    useGlobalGifts,
    useProspectingUser,
    useReceivedMemberAudioGifts,
    useReceivedProspectAudioGifts,
} from "JS/React/Hooks/Gifts";
import { useGlobalAudios, useAudios } from "JS/React/Hooks/Audio";
import { AppHeader } from "JS/React/Components/AppHeader";
import { AudioCategoryList } from "JS/React/Scenes/Audios/Components/AudioCategoryList";
import { uniqBy } from "lodash-es";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { messaging } from "JS/Helpers/UserMessaging";
import { Content, ReceivedGift, SelectedPlaylistContent } from "JS/Models";
import { useGlobalNavStack } from "JS/React/Hooks/NavStack";
import { useGetFavorites, useGlobalFavorites } from "JS/React/Hooks/Favorites";
import {
    useFilteredRecommendations,
    useGlobalRecommendations,
    useRecommendations,
} from "JS/React/Hooks/Recommendations";
import { AttributeType } from "JS/Redux/Attributes";
import { useAttributes, useGlobalAttributes } from "JS/React/Hooks/Attributes";
import { MultiAutocompleteProps } from "JS/React/Components/AppMultiTagSelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SpeakerPlaylistDialog } from "./SpeakerPlaylistDialog";
import { useCreatePlaylistWithContents } from "JS/React/Hooks/Playlist";
import { sortData, sortViaAcquiredDate, sortViaPlayedDate } from "JS/Types";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useScrollWithDelay } from "JS/React/Hooks/Media";
import { useGlobalMediaEssentials } from "JS/React/Hooks/MediaEssentials";
import { ListingComponent } from "./ListingComponent";
import { useAudiosHandlers } from "./AudioHooks";
import { useAudioResumeHelper } from "./AudioHelpers";
import { useDownloadedContentStatus } from "../Videos/DownloadedContentStatus";
import { ContentAttribute } from "JS/Models/Attributes";

export interface AudiosProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {}

export enum AudioCategoryTitle {
    ALL_AUDIOS = "all-audios",
    MY_AUDIOS = "my-audios",
    SAVED = "saved-audios",
    RECENTLY_PLAYED = "recently_played",
    RECOMMENDED = "recommended",
    INDEX = "index",
}

export const Audios = (props: AudiosProps) => {
    const classes = useStyles(props);
    const { className, ...rest } = props;

    const { audios, categories } = useGlobalAudios();

    const { favNids } = useGlobalFavorites();
    const { isResumable } = useAudioResumeHelper();

    useGetFavorites(!!favNids?.length);

    const { loading: audiosLoading } = useAudios();
    const { isProsUser, loading: loadingProsStatus } = useProspectingUser();

    const { receivedMemberAudios, receivedProspectAudios } = useGlobalGifts();
    useReceivedMemberAudioGifts();
    useReceivedProspectAudioGifts(!isProsUser);

    const { setGlobalAudiosStack, resetGlobalAudiosStack, audiosStack } =
        useGlobalNavStack();

    const { filteredRecommendedAudios, recommendationsLoading } =
        useAudioRecommendations(audios);

    const [searchQuery, setSearchQuery] = useState(audiosStack?.searchQuery);

    const {
        audioUniqueSpeakers,
        audioSelectedSpeakers,
        attributesLoading,
        onToggleSpeakerSelect,
        handleClearFilters,
        handleResetAudioAttributes,
        mediaEssentials,
    } = useAudioAttributesHandlers();

    const [showList, setShowList] = useState(false);

    const filteredAudios = useMemo(() => {
        return uniqBy(
            [
                ...audios,
                ...(receivedMemberAudios || []),
                ...(receivedProspectAudios || []),
            ],
            (x) => x.nid,
        );
    }, [audios, receivedMemberAudios, receivedProspectAudios]);

    const { searchResults } = useFilterAudioHandlers(
        filteredAudios,
        searchQuery,
        audioSelectedSpeakers,
        audioUniqueSpeakers,
    );

    const {
        createClicked,
        handleCreateClick,
        handleClose,
        handleCreatePlaylist,
        onToggleSelectItem,
        selectedContentNids,
        finalData,
        createPlaylistLoading,
        playlistName,
        setPlaylistName,
    } = useCreatePlaylistHandlers(searchResults);

    useEffect(() => {
        setGlobalAudiosStack({
            searchQuery: searchQuery,
        });
    }, [searchResults, audioSelectedSpeakers]);

    const loaded =
        !audiosLoading &&
        !recommendationsLoading &&
        !attributesLoading &&
        !loadingProsStatus;

    const audioListVisible =
        showList ||
        searchQuery?.length > 0 ||
        audioSelectedSpeakers?.length > 0;
    const { handleRedirectToDetail } = useAudiosHandlers(
        searchQuery,
        AudioCategoryTitle.INDEX,
    );
    const useAudiosDownloadHelperData = (data: (Content | ReceivedGift)[]) => {
        const { getDownloadedStatusById } = useDownloadedContentStatus(
            data,
            "audio",
        );
        return getDownloadedStatusById;
    };
    useScrollWithDelay();

    return (
        <Layout>
            <div className={clsx(className, classes.root)} {...rest}>
                <AppHeader
                    title="Audios"
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    searchPlaceholder={"Search Audios by title/description"}
                    onSearchFieldClick={() => {
                        setShowList(true);
                    }}
                    onBackClick={() => {
                        setSearchQuery("");
                        resetGlobalAudiosStack();
                        handleResetAudioAttributes();
                        setShowList(false);
                    }}
                    canGoBack={audioListVisible}
                    filterable={mediaEssentials?.allow_speaker_feature}
                    onToggleSpeakerSelect={onToggleSpeakerSelect}
                    uniqueSpeakers={audioUniqueSpeakers}
                    selectedSpeakers={audioSelectedSpeakers}
                    handleClearFilters={handleClearFilters}
                />
                {loaded ? (
                    <>
                        {audioListVisible ? (
                            <>
                                {mediaEssentials?.allow_speaker_feature &&
                                    audioSelectedSpeakers?.length > 0 &&
                                    searchResults?.length > 0 && (
                                        <Box
                                            className={clsx(
                                                className,
                                                classes.fab,
                                            )}
                                        >
                                            <Fab
                                                color="primary"
                                                aria-label="add"
                                                className={clsx(
                                                    className,
                                                    classes.floatingButton,
                                                )}
                                                onClick={handleCreateClick}
                                            >
                                                <FontAwesomeIcon
                                                    icon={["fas", "music"]}
                                                />
                                            </Fab>
                                        </Box>
                                    )}

                                <ListingComponent
                                    listContent={searchResults as Content[]}
                                    searchQuery={searchQuery}
                                    categoryId={AudioCategoryTitle.INDEX}
                                    showSortHeader={true}
                                    setGlobalStack={setGlobalAudiosStack}
                                    globalStack={audiosStack}
                                    isResumeable={isResumable}
                                    favoritesContent={favNids}
                                    handleListItemClick={handleRedirectToDetail}
                                    getDownloadHelperData={
                                        useAudiosDownloadHelperData
                                    }
                                    controlType="audio"
                                />
                                {createClicked && (
                                    <SpeakerPlaylistDialog
                                        open={createClicked}
                                        audios={finalData}
                                        handleClose={handleClose}
                                        handleCreatePlaylist={
                                            handleCreatePlaylist
                                        }
                                        onToggleSelectItem={onToggleSelectItem}
                                        selectedContentNids={
                                            selectedContentNids
                                        }
                                        sortType={audiosStack?.sorting[0].col}
                                        playlistName={playlistName}
                                        setPlaylistName={setPlaylistName}
                                        createPlaylistLoading={
                                            createPlaylistLoading
                                        }
                                    />
                                )}
                            </>
                        ) : (
                            <AudioCategoryList
                                categorizedAudios={categories}
                                recommendations={filteredRecommendedAudios}
                            />
                        )}
                    </>
                ) : (
                    <AppBackdropProgress
                        open={true}
                        backdropText={
                            !attributesLoading
                                ? messaging.loader.audios
                                : messaging.loader.fectchingSpeakers
                        }
                    />
                )}
            </div>
        </Layout>
    );
};

const useAudioRecommendations = (audios: Content[]) => {
    const { recommendationState } = useGlobalRecommendations();

    const { loading: recommendationsLoading } = useRecommendations(
        recommendationState?.recommendations != null,
    );

    const { filteredRecommendedAudios } = useFilteredRecommendations(
        recommendationState?.recommendations,
        audios,
    );

    return {
        filteredRecommendedAudios,
        recommendationsLoading,
    };
};

export const useAudioAttributesHandlers = () => {
    const {
        audioFetchStatus,
        audioUniqueSpeakers,
        audioSelectedSpeakers,
        setAudioSelectedSpeakers,
        resetAudioSelectedSpeakers,
    } = useGlobalAttributes();
    const { mediaEssentials } = useGlobalMediaEssentials();

    const { loading: attributesLoading } = useAttributes(
        AttributeType.AUDIO,
        audioFetchStatus || !mediaEssentials?.allow_speaker_feature,
    );

    const onToggleSpeakerSelect: MultiAutocompleteProps["onChange"] = (
        _,
        value,
    ) => setAudioSelectedSpeakers(value);

    const handleClearFilters = () => resetAudioSelectedSpeakers();

    const handleResetAudioAttributes = () => resetAudioSelectedSpeakers();

    return {
        mediaEssentials,
        audioUniqueSpeakers,
        audioSelectedSpeakers,
        attributesLoading,
        onToggleSpeakerSelect,
        handleClearFilters,
        handleResetAudioAttributes,
    };
};

export const useCreatePlaylistHandlers = (
    searchResults: (Content | ReceivedGift)[],
) => {
    const [createClicked, setCreateClicked] = useState<boolean>(false);
    const [selectedPlaylistContent, setSelectedPlaylistContent] = useState<
        SelectedPlaylistContent[]
    >([]);
    const [playlistName, setPlaylistName] = useState<string>("");
    const { audiosStack } = useGlobalNavStack();
    const { createPlaylistWithContents, loading: createPlaylistLoading } =
        useCreatePlaylistWithContents();

    const { enqueueSnackbar } = useSnackbar();

    const { showPlaylistSnackbar, incrementPlaylistSnackCount } =
        useGlobalAttributes();

    const playableContent = useMemo(() => {
        return searchResults.filter(
            (x) => x.is_purchased || x.isReceived || x.publish_free === "1",
        );
    }, [searchResults]);

    const finalData = useMemo(() => {
        if (audiosStack?.sorting[0].col === "acquired_date") {
            const newContent = playableContent?.map((d) => {
                if ("sender" in d) {
                    const timeStamp = moment(d.sender.sending_date_time)
                        .valueOf()
                        .toString();
                    return {
                        ...d,
                        acquired_date: timeStamp,
                    };
                }
                return d;
            });
            return sortViaAcquiredDate(newContent, audiosStack?.sorting);
        }
        if (audiosStack?.sorting[0].col === "played")
            return sortViaPlayedDate(playableContent, audiosStack?.sorting);

        return sortData(playableContent, audiosStack?.sorting);
    }, [playableContent, audiosStack?.sorting]);

    useEffect(() => {
        setSelectedPlaylistContent(
            finalData.map((x) => {
                return {
                    nid: x.nid,
                    checked: true,
                };
            }),
        );
    }, [finalData, createClicked]);

    const handleClose = () => {
        setCreateClicked(false);
        setPlaylistName("");
    };

    const handleCreatePlaylist = async () => {
        if (
            playlistName &&
            playlistName.length <= 45 &&
            selectedPlaylistContent?.length > 0
        ) {
            await createPlaylistWithContents(
                playlistName,
                searchResults.filter(s => selectedPlaylistContent.filter(p => p.checked && p.nid === s.nid)).slice(0, 400)
            ).then(() => {
                setPlaylistName("");
                setCreateClicked(false);
            });
        } else {
            if (selectedPlaylistContent?.length === 0) {
                enqueueSnackbar("Please select at-least one audio", {
                    variant: "warning",
                });
            } else {
                enqueueSnackbar(
                    !playlistName || playlistName?.length === 0
                        ? messaging?.playlist?.emptyInput
                        : messaging?.playlist?.characterInput,
                    {
                        variant: "warning",
                    },
                );
            }
        }
    };

    const onToggleSelectItem = (nid: string, checked: boolean) => {
        let tempArray = [...selectedPlaylistContent];
        if (selectedPlaylistContent.find((x) => x.nid === nid) && !checked) {
            tempArray[
                selectedPlaylistContent.findIndex((x) => x.nid === nid)
            ].checked = false;
            setSelectedPlaylistContent(tempArray);
        } else {
            tempArray[
                selectedPlaylistContent.findIndex((x) => x.nid === nid)
            ].checked = true;
            setSelectedPlaylistContent(tempArray);
        }
    };
    const selectedContentNids = useMemo(() => {
        return selectedPlaylistContent
            .filter((x) => x.checked === true)
            .map((x) => x.nid);
    }, [selectedPlaylistContent]);

    const handleCreateClick = () => {
        if (finalData?.length > 0) {
            if (showPlaylistSnackbar) {
                enqueueSnackbar(messaging?.playlist?.createPlaylistInfo, {
                    variant: "info",
                    autoHideDuration: 4000,
                });
                incrementPlaylistSnackCount();
            }
            setCreateClicked(true);
        } else {
            enqueueSnackbar(messaging?.playlist?.requiredOneContent, {
                variant: "warning",
            });
        }
    };
    return {
        createClicked,
        handleCreateClick,
        handleClose,
        handleCreatePlaylist,
        onToggleSelectItem,
        finalData,
        selectedContentNids,
        createPlaylistLoading,
        playlistName,
        setPlaylistName,
    };
};

export const useFilterAudioHandlers = (
    filteredAudios: (Content | ReceivedGift)[],
    searchQuery: string,
    audioSelectedSpeakers: string[],
    audioContentSpeakers: ContentAttribute[],
) => {
    const filterNids = useMemo(() => {
        return audioContentSpeakers
            ? audioContentSpeakers.filter((x) =>
                  x.attributeValues.some((z) =>
                      audioSelectedSpeakers.includes(z),
                  ),
              )
            : [];
    }, [audioSelectedSpeakers, audioContentSpeakers]);

    const filteredResults = useMemo(() => {
        return filterNids.length > 0
            ? filteredAudios?.filter((d) =>
                  filterNids.some((z) => z.nid.toString() === d.nid),
              )
            : filteredAudios;
    }, [filteredAudios, filterNids]);

    const searchResults = useMemo(() => {
        return filteredResults?.filter(
            (d) =>
                d &&
                (d.title
                    .toLocaleLowerCase()
                    .includes(searchQuery.toLocaleLowerCase().trim()) ||
                    d.description
                        .toLocaleLowerCase()
                        .includes(searchQuery.toLocaleLowerCase().trim())),
        );
    }, [filteredResults, searchQuery]);

    return { searchResults };
};
const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            padding: theme.spacing(1),
        },
        wrapperLoader: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "90vh",
        },
        createPlaylistButton: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: theme.spacing(2),
        },
        plusIcon: {
            height: "20px",
            width: "20px",
            padding: theme.spacing(1),
        },
        fab: {
            position: "fixed",
            right: "10px",
            bottom: `calc(${theme.footer.height} + 10px + env(safe-area-inset-bottom, 0px))`,
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            zIndex: "1",
        },
        floatingButton: {
            background: theme.palette.primary.main,
            "&:hover": {
                background: theme.palette.primary.main,
            },
        },
    }),
);
